import { useApiQuery } from '@/hooks/useApiQuery';
import { useHasAnyPermission } from '@/hooks/usePermissions';
import { apiGet, operationsClient } from '@/lib/api/client';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import type { CompanySettings } from '@/types/settings';

export type CompanyDisplayName =
  | { status: 'loading'; name: null }
  | { status: 'ready'; name: string }
  | { status: 'unavailable'; name: null; reason: 'no-name' | 'no-access' }
  | { status: 'unavailable'; name: null; reason: 'failed'; retry: () => void };

/**
 * The company's display name: `CompanySettings.name`, the name the tenant
 * prints on its documents and sends customer email from. Use this, not the
 * tenant store's `tenantName`, wherever customer-facing text names the
 * business: `tenantName` is only filled in on some login paths and is '' after
 * a normal password login.
 *
 * Reads the letterhead route, which admits `company:profile_view` (held by the
 * everyday staff roles) as well as `company:view`. `useCompanySettings` asks
 * for `company:view` alone, so it has no name for most of the staff who take
 * payments. Same query key and response, so the two share one cache entry.
 *
 * Unavailable says why, because each reason asks for a different next step:
 * - `no-name`: the tenant has not set a company name (an admin can add one);
 * - `no-access`: this user may not read it — the SPA knows the role lacks the
 *   permission, or the server refused the read (403). Permanent for this
 *   session, so there is nothing to retry;
 * - `failed`: the read failed for some other reason, and `retry()` asks again.
 *
 * Never falls back to a placeholder. A caller that can't show the real name
 * must say so; it must not print "your company" into text a customer agrees to.
 */
export function useCompanyDisplayName(): CompanyDisplayName {
  const canViewProfile = useHasAnyPermission('company:view', 'company:profile_view');
  const query = useApiQuery<CompanySettings>({
    queryKey: settingsKeys.company(),
    queryFn: () => apiGet<CompanySettings>(operationsClient, settingsEndpoints.company()),
    enabled: canViewProfile,
    showErrorToast: false,
  });

  if (!canViewProfile) return { status: 'unavailable', name: null, reason: 'no-access' };
  const name = query.data?.name?.trim();
  if (name) return { status: 'ready', name };
  if (query.isPending || (query.isFetching && !query.data)) return { status: 'loading', name: null };
  if (query.data) return { status: 'unavailable', name: null, reason: 'no-name' };
  if (query.error?.status === 403) return { status: 'unavailable', name: null, reason: 'no-access' };
  return {
    status: 'unavailable',
    name: null,
    reason: 'failed',
    retry: () => void query.refetch(),
  };
}
